import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DxDataGridComponent } from 'devextreme-angular';
import { confirm } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { AppInformationService, AuthenticationService, RAFUnitOfWork } from 'src/app/core/core.module';
import { Job, JobStatus, JobType } from 'src/app/core/data/entities';
import { JobDialogComponent } from '../job-dialog/job-dialog.component';
import { InputDialogComponent } from 'src/app/shared/shared.module';

@Component({
    selector: 'app-job-search',
    templateUrl: './job-search.component.html',
    styleUrls: ['./job-search.component.scss'],
    standalone: false
})
export class JobSearchComponent implements OnInit {
    jobs: any;
    workOrderList: any[];
    showAllOrders = false;
    showActiveJobs: boolean = true;
    filter: any;
    jobTypes: JobType[];

    salesMode = false;
    ordersMode = false;
    claimButtonDisable = true;
    unclaimButtonDisable = true;
    woAddedSubscription: Subscription;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: RAFUnitOfWork,
        private router: Router,
        public auth: AuthenticationService,
        private appInfo: AppInformationService,
        private modalService: BsModalService
    ) {}

    async ngOnInit() {
        const statuses = await this.uow.lookups.statuses;
        this.jobTypes = await this.uow.getActiveJobTypes();
        await this.getData();
    }

    async getData() {
        if (this.showActiveJobs) this.jobs = await this.uow.getActiveJobs();
        else this.jobs = await this.uow.getJobs();
    }

    async toggleShowActive() {
        this.showActiveJobs = !this.showActiveJobs;
        await this.getData();
    }

    print() {
        const selectedInvoices = this.dataGrid.instance.getSelectedRowsData();
        if (selectedInvoices.length === 0) return;
        const invoiceIds = selectedInvoices.map((x) => {
            return x.id;
        });
        // this.reportService.openSalesReport(invoiceIds);
    }

    search() {}
    clear() {}

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        const filter = e.component.getCombinedFilter();
        if (e.component.__searchChanged || !!filter) {
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    selectionChange(e) {}

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons'
            },
            {
                template: 'toolbarText',
                location: 'after'
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    }
                },
                location: 'after'
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    }
                },
                location: 'after'
            }
        );
    }
    async rowDoubleClick(e) {
        if (!e.data) return;
        await this.open(e.data.id);
    }
    rowPrepare(e) {
        if (e.rowType === 'data') {
            // if (!!e.data.highPriority) {
            //     e.rowElement.className = e.rowElement.className + ' red-grid';
            // }
            if (e.data.jobType.color) {
                // console.log('data', e);
                e.rowElement.style.backgroundColor = e.data.jobType.color;
            }
        }
    }
    async open(id?: number) {
        this.router.navigate(['/jobs/' + (!!id ? id : 'new')]);
    }

    async addJob(job?: any) {
        const modalRef = this.modalService.show(JobDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                jobId: !!job ? job.id : null
            }
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }

    async delete(selection: any) {
        const result = await (confirm('Are you sure you want to DELETE this job?', `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!!result) {
            this.appInfo.pleaseWaitShow();
            selection.deleted = new Date();
            await this.uow.commit();
            await this.getData();
            this.appInfo.pleaseWaitHide();
        }
    }

    async updateStatus() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const result = await (confirm(
            `Are you sure you want to COMPLETE ${selectedRows.length === 1 ? 'this order' : 'these orders'}?`,
            `${this.appInfo.settings.company}`
        ) as Promise<boolean>);
        if (!result) return;

        const orderIds = selectedRows.map((x) => {
            return x.id;
        });

        selectedRows.forEach((row) => {
            row.statusId = 1;
        });
        await this.uow.commit();
        await this.getData();
    }

    async completeJobs() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData() as Job[];
        if (selectedRows.length === 0) return;

        if (!(await confirm(`Are you sure you want to COMPLETE ${selectedRows.length > 1 ? 'these jobs' : 'this job'}?`, `${this.appInfo.settings.company}`))) return;

        selectedRows.forEach((j) => {
            j.completed = new Date();
            j.completedById = this.auth.userValue.id;
            j.statusId = JobStatus.Statuses.Complete;
            j.jobTypeId = 4;
        });
        await this.uow.commit();
    }

    async editJobType(jobs: Job[]) {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'selectWithId',
                dialogHeader: 'Edit Job Type',
                dialogPrompt: 'Job Type',
                dialogValue: jobs.length === 1 ? jobs[0].jobTypeId : null,
                dialogValueArray: this.jobTypes
            }
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.value) {
                this.appInfo.pleaseWaitShow();
                jobs.forEach((job) => (job.jobTypeId = result.value));
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
            this.appInfo.pleaseWaitHide();
        });
    }
}
